import { useForm } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import useParkingService from '../../../hooks/parking-services/useParkingService.ts';
import useParkingServiceImages from '../../../hooks/parking-services/useParkingServiceImages.ts';
import useAddParkingServiceImage from '../../../hooks/parking-services/useAddParkingServiceImage.ts';
import useRemoveParkingServiceImage from '../../../hooks/parking-services/useRemoveParkingServiceImage.ts';
import {
  AddParkingServiceImageRequest,
  RemoveParkingServiceImageRequest,
} from '../../../api/requests/parking-service-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { Dropzone, FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import {
  Button,
  Flex,
  Group,
  Image,
  LoadingOverlay,
  Stack,
  Text,
} from '@mantine/core';
import {
  IconCloudUpload,
  IconPlus,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import React from 'react';

export interface MutateParkingServiceImagesFormValues {
  parkingServiceId: string;
}

interface MutateParkingServiceImagesFormProps {
  initialValues: MutateParkingServiceImagesFormValues;
  onSubmit: (formValues: MutateParkingServiceImagesFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
  isUpdating: boolean;
}

export default function MutateParkingServiceImagesForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting,
  isUpdating,
}: MutateParkingServiceImagesFormProps) {
  const form = useForm<MutateParkingServiceImagesFormValues>({
    mode: 'controlled',
    initialValues: {
      parkingServiceId: initialValues.parkingServiceId,
    },
  });

  const queryClient = useQueryClient();

  const { data: parkingService, isLoading: isParkingServiceLoading } =
    useParkingService(initialValues.parkingServiceId);

  const parkingServiceImageQueries = useParkingServiceImages(
    parkingService?.id,
    parkingService?.imageIds,
  );

  const isAnyImageLoading = parkingServiceImageQueries.some((q) => q.isLoading);

  const { mutateAsync: addParkingServiceImage } = useAddParkingServiceImage();
  const { mutateAsync: removeParkingServiceImage } =
    useRemoveParkingServiceImage();

  const handleParkingServiceImageRemoveClick = async (q) => {
    try {
      const removeParkingServiceImageRequest: RemoveParkingServiceImageRequest =
        {
          serviceId: parkingService.id,
          imageId: q.data.imageId.toString().replaceAll('/', '%2F'),
        };
      await removeParkingServiceImage(removeParkingServiceImageRequest);
      await queryClient.invalidateQueries({
        queryKey: ['parkingServices', parkingService.id],
        exact: false,
      });
    } catch (error) {
      notifyError('刪除失敗', error.message);
    }
  };

  const handleParkingServiceImageAddClick = async (files: FileWithPath[]) => {
    if (files.length === 0) return;

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const addParkingServiceImageRequest: AddParkingServiceImageRequest = {
          serviceId: parkingService.id,
          file: formData,
        };
        await addParkingServiceImage(addParkingServiceImageRequest);
        await queryClient.invalidateQueries({
          queryKey: ['parkingServices', parkingService.id],
          exact: false,
        });
      } catch (error) {
        notifyError('上傳失敗', error.message);
      }
    }
  };

  const handleSubmit = form.onSubmit((values) => {
    onSubmit(values);
  });

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  if (isParkingServiceLoading || isAnyImageLoading)
    return <LoadingOverlay visible={false} />;

  return (
    <>
      <form onSubmit={handleSubmit}>
        {parkingServiceImageQueries?.length ? (
          <Flex
            gap={9}
            style={{
              minHeight: 160,
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              overflowX: 'hidden',
              overflowY: 'auto',
              alignContent: 'flex-start',
            }}
          >
            {parkingServiceImageQueries?.map((q) => (
              <div
                key={q.data.imageId}
                style={{
                  width: 150,
                  height: 150,
                  borderRadius: 8,
                  position: 'relative',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                <Image
                  src={q.data.signedUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                {/* 刪除按鈕 */}
                <Button
                  size={'xs'}
                  variant={'filled'}
                  color={'red'}
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    padding: '2px 6px',
                    zIndex: 2,
                  }}
                  onClick={() => handleParkingServiceImageRemoveClick(q)}
                >
                  x
                </Button>
              </div>
            ))}

            {/* 新增圖片按鈕 */}
            <div
              style={{
                width: 150,
                height: 150,
                borderRadius: 8,
                backgroundColor: '#F8F9FA',
                alignContent: 'center',
                flexShrink: 0,
                cursor: 'pointer',
              }}
            >
              <Dropzone
                onDrop={(files) => handleParkingServiceImageAddClick(files)}
                onReject={(files) => console.log('rejected files', files)}
                maxSize={5 * 1024 ** 2}
                accept={[MIME_TYPES.png]}
                style={{
                  width: '100%',
                  height: '100%',
                  border: '1px dashed var(--mantine-color-gray-4)',
                  borderRadius: 8,
                  padding: 0,
                }}
                styles={{
                  inner: {
                    height: '100%',
                  },
                }}
              >
                <Group justify={'center'} style={{ height: '100%' }}>
                  <Stack align={'center'}>
                    <IconPlus size={24} color={'#868E96'} />
                    <Text
                      style={{
                        fontSize: 16,
                        fontWeight: 500,
                        color: '#868E96',
                      }}
                    >
                      新增圖片
                    </Text>
                  </Stack>
                </Group>
              </Dropzone>
            </div>
          </Flex>
        ) : (
          <Dropzone
            onDrop={(files) => handleParkingServiceImageAddClick(files)}
            onReject={(files) => console.log('rejected files', files)}
            maxSize={5 * 1024 ** 2}
            accept={[MIME_TYPES.png]}
            style={{
              border: '1px dashed var(--mantine-color-gray-4)',
              borderRadius: 8,
              padding: 0,
              height: 221,
            }}
          >
            <Group justify={'center'} gap={'xl'} mih={220}>
              <Stack align="center">
                <Dropzone.Accept>
                  <IconUpload size={52} stroke={1.5} />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX size={52} stroke={1.5} />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconCloudUpload size={52} color="#212529" stroke={1.5} />
                </Dropzone.Idle>
                <div>
                  <Text size="lg" fw={500} inline mb={12} ta="center">
                    上傳圖片
                  </Text>
                  <Text size="sm" c="dimmed" inline mt={7} ta="center">
                    把 .png 檔案拖曳到這裡上傳,或是選擇圖片。
                  </Text>
                </div>
              </Stack>
            </Group>
          </Dropzone>
        )}

        <Group justify={'flex-end'} mt={'24px'}>
          <Button variant={'outline'} onClick={handleCancel}>
            取消
          </Button>
          <Button
            type={'submit'}
            loading={isSubmitting}
            loaderProps={{ type: 'oval', size: 'xs' }}
          >
            {isUpdating ? '更新' : '下一步'}
          </Button>
        </Group>
      </form>
    </>
  );
}
